import { Link } from "react-router-dom";
import { StatusChip, type StatusTone } from "@/components/ui";
import { Text } from "@/components/typography";
import type { Person, SlaState, SubjectView } from "./deskApi";
import { SUBJECT_LABEL } from "./deskApi";
import styles from "./desk.module.css";

export function timeAgo(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const d = Math.round((now - t) / 1000);
  const abs = Math.abs(d);
  const fmt = (n: number, u: string) => `${n}${u}`;
  const v =
    abs < 60 ? fmt(abs, "s")
    : abs < 3600 ? fmt(Math.round(abs / 60), "m")
    : abs < 86400 ? fmt(Math.round(abs / 3600), "h")
    : fmt(Math.round(abs / 86400), "d");
  return d >= 0 ? `${v} ago` : `in ${v}`;
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function priorityTone(p: string): StatusTone {
  return p === "critical" || p === "urgent" ? "danger" : p === "high" ? "warning" : p === "low" ? "neutral" : "info";
}

export function SlaChip({ state, dueAt }: { state: SlaState | string; dueAt: string | null }) {
  if (state === "none" || !dueAt) return null;
  if (state === "breached") return <StatusChip tone="danger">SLA breached {timeAgo(dueAt)}</StatusChip>;
  if (state === "due_soon") return <StatusChip tone="warning">Due {timeAgo(dueAt)}</StatusChip>;
  if (state === "met") return <StatusChip tone="success">SLA met</StatusChip>;
  return <StatusChip tone="neutral">Due {timeAgo(dueAt)}</StatusChip>;
}

export function personName(p: Person | null | undefined): string {
  if (!p) return "Unknown";
  return p.display_name ?? (p.handle ? `@${p.handle}` : `${p.id.slice(0, 8)}…`);
}

export function PersonChip({ person, fallback = "—", link = true }: { person: Person | null | undefined; fallback?: string; link?: boolean }) {
  if (!person) return <span>{fallback}</span>;
  const initial = (person.display_name ?? person.handle ?? "?").slice(0, 1).toUpperCase();
  const body = (
    <span className={styles.authorRow}>
      {person.avatar ? <img src={person.avatar} alt="" className={styles.avatar} /> : <span className={styles.avatar}>{initial}</span>}
      <span>
        <Text role="bodyStrong" as="span">{personName(person)}</Text>
        {person.handle ? <Text role="caption" color="muted" as="span">{`  @${person.handle}`}</Text> : null}
        {person.account_status && person.account_status !== "active" ? (
          <Text role="caption" color="secondary" as="div">Account: {person.account_status}</Text>
        ) : null}
      </span>
    </span>
  );
  return link ? <Link to={`/app/users/${person.id}`}>{body}</Link> : body;
}

/** The reported content as the reporter saw it. */
export function SubjectRender({ subject, snapshot }: { subject: SubjectView; snapshot?: Record<string, unknown> | null }) {
  const label = SUBJECT_LABEL[subject.subject_type] ?? subject.subject_type;
  const snapText = snapshot
    ? [snapshot.title, snapshot.body, snapshot.caption, snapshot.name].filter((v) => typeof v === "string" && v).join(" — ")
    : "";
  return (
    <div className={styles.contentFrame}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <StatusChip tone="info">{label}{subject.where ? ` · ${subject.where.surface}` : ""}</StatusChip>
        {subject.where?.community_name ? (
          <Text role="small" color="secondary" as="span">
            in{" "}
            {subject.where.community_id ? (
              <Link to={`/app/communities/${subject.where.community_id}`}>{subject.where.community_name}</Link>
            ) : subject.where.community_name}
          </Text>
        ) : null}
      </div>

      {subject.removed ? (
        <div className={styles.removedBanner}>
          <Text role="small" as="span">
            Removed {fmtDate(subject.removed.removed_at)} — hidden from everyone in Play. Shown here for review only.
          </Text>
        </div>
      ) : null}

      {!subject.exists ? (
        <div className={styles.removedBanner}>
          <Text role="small" as="p">
            The original is gone (deleted by its author). What was reported, as captured at the time:
          </Text>
          <p className={styles.bodyText}>{snapText || "Nothing was captured for this kind of report."}</p>
        </div>
      ) : (
        <>
          {subject.context.filter((c) => c.kind === "parent_post").map((c) => (
            <div key={c.id} className={styles.contextItem}>
              <Text role="caption" color="muted" as="div">Replying to a post by {c.author ?? "someone"} · {fmtDate(c.posted_at)}</Text>
              <div className={styles.bodyText}>{c.text ?? "(no text)"}</div>
            </div>
          ))}
          {subject.context.filter((c) => c.kind === "earlier_message").length > 0 ? (
            <Text role="caption" color="muted" as="div">Messages just before it (context only):</Text>
          ) : null}
          {subject.context.filter((c) => c.kind === "earlier_message").map((c) => (
            <div key={c.id} className={styles.contextItem}>
              <Text role="caption" color="muted" as="div">{c.author ?? "Someone"} · {fmtDate(c.posted_at)}</Text>
              <div className={styles.bodyText}>{c.text ?? ""}</div>
            </div>
          ))}
          <div className={subject.subject_type === "chat_message" ? `${styles.contextItem} ${styles.contextReported}` : undefined}>
            {subject.owner ? <PersonChip person={subject.owner} /> : null}
            <Text role="caption" color="muted" as="div">Posted {fmtDate(subject.posted_at)}</Text>
            {subject.title ? <Text role="title" as="h3">{subject.title}</Text> : null}
            {subject.text ? <p className={styles.bodyText}>{subject.text}</p> : null}
            {!subject.title && !subject.text && subject.media.length === 0 ? (
              <Text role="small" color="muted" as="p">No text.</Text>
            ) : null}
          </div>
          {subject.media.map((m, i) => (
            <div key={i}>
              {m.url ? (
                m.kind === "video" ? (
                  <video className={styles.media} src={m.url} poster={m.thumbnail_url ?? undefined} controls preload="metadata" />
                ) : (
                  <img className={styles.media} src={m.url} alt="Reported media" />
                )
              ) : null}
              {m.note ? <Text role="caption" color="muted" as="div">{m.note}</Text> : null}
            </div>
          ))}
          {Object.entries(subject.facts).filter(([, v]) => v !== null && v !== "").length > 0 ? (
            <Text role="small" color="secondary" as="div">
              {Object.entries(subject.facts)
                .filter(([, v]) => v !== null && v !== "")
                .map(([k, v]) => `${k.replace(/_/g, " ")}: ${String(v)}`)
                .join(" · ")}
            </Text>
          ) : null}
          {subject.hq_path ? (
            <Link to={subject.hq_path}>
              <Text role="small" color="link" as="span">Open in HQ →</Text>
            </Link>
          ) : null}
        </>
      )}
    </div>
  );
}

export function Kpi({ label, value, tone, onClick, hint }: { label: string; value: number | string; tone?: "danger" | "warn" | undefined; onClick?: () => void; hint?: string }) {
  const cls = [styles.kpi, tone === "danger" ? styles.kpiDanger : tone === "warn" ? styles.kpiWarn : ""].join(" ");
  const inner = (
    <>
      <Text role="caption" color="secondary" as="span">{label}</Text>
      <span className={styles.kpiValue}>{value}</span>
      {hint ? <Text role="caption" color="muted" as="span">{hint}</Text> : null}
    </>
  );
  return onClick ? (
    <button type="button" className={cls} onClick={onClick}>{inner}</button>
  ) : (
    <div className={cls}>{inner}</div>
  );
}
